import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Skeleton,
  useToast,
  Image,
  Box,
} from "@chakra-ui/react";
import { useEffect } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

import Card from "components/card/Card";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { useHistory } from "react-router-dom";
import { PartnerNotification } from "../store";
import { deleteNotification } from "../services";
import usePartnerNotificationStore from "../store";
import { useConfirmDialog } from "components/confirmModal";
import moment from "moment";

export default function ColumnsTable(props: {
  columnsData: any;
  tableData: any;
  isLoading: boolean;
}) {
  const { columnsData, tableData, isLoading } = props;

  const history = useHistory();
  const toast = useToast();
  const store = usePartnerNotificationStore();

  const { ConfirmDialog, openDialog } = useConfirmDialog({
    title: "Notification is being removed",
    description:
      "You are about to remove the notification. This action cannot be undone.",
    destructive: true,
  });

  const tableInstance = useTable<PartnerNotification>(
    {
      columns: columnsData,
      data: tableData,
      initialState: { pageSize: 200 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    initialState,
  } = tableInstance;
  initialState.pageSize = 200;

  useEffect(() => {
    tableInstance.setPageSize(200);
  }, [tableData, columnsData, isLoading, tableInstance]);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const handleDelete = async (id: string, isPastNotification: boolean) => {
    try {
      if (isPastNotification) {
        toast({
          title: "Cannot Delete",
          description: "Past notifications cannot be deleted",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const result = await openDialog();
      if (!result) return;

      await deleteNotification(id);
      store.setShouldRefresh(true);
      toast({
        title: "Success",
        description: "Notification deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete notification",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      {ConfirmDialog}
      <Card
        flexDirection="column"
        w="100%"
        px="0px"
        overflowX={{ sm: "scroll", lg: "scroll" }}
      >
        <Flex px="25px" justify="space-between" mb="10px" align="center">
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Notification Manager
          </Text>
        </Flex>
        <Table
          {...getTableProps()}
          variant="striped"
          color="gray.500"
          mb="24px"
        >
          <Thead>
            {headerGroups.map((headerGroup, index) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column, index) => (
                  <Th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    pe="10px"
                    key={index}
                    borderColor={borderColor}
                  >
                    <Flex
                      justify="space-between"
                      align="center"
                      fontSize={{ sm: "10px", lg: "12px" }}
                      color="gray.400"
                    >
                      {column.render("Header")}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              return (
                <Tr {...row.getRowProps()} key={index}>
                  {row.cells.map((cell, index) => {
                    let data;
                    if (cell.column.Header === "EDIT") {
                      data = (
                        <EditIcon
                          color="black"
                          cursor="pointer"
                          alignSelf={"center"}
                          onClick={() =>
                            history.push(
                              `/notification-manager/create?id=${cell.row.original.id}`
                            )
                          }
                        />
                      );
                    } else if (cell.column.Header === "DELETE") {
                      const isPastNotification = moment(
                        cell.row.original.scheduledAt
                      ).isBefore(moment(), "minute");
                      data = (
                        <DeleteIcon
                          color="red.500"
                          cursor={"pointer"}
                          alignSelf={"center"}
                          opacity={isPastNotification ? 0.5 : 1}
                          onClick={() =>
                            handleDelete(
                              cell.row.original.id.toString(),
                              isPastNotification
                            )
                          }
                        />
                      );
                    } else if (cell.column.Header === "Image") {
                      data = (
                        <Flex height={"48px"}>
                          {cell.value ? (
                            <Image
                              width={"48px"}
                              src={cell.value}
                              alt="Notification Image"
                              borderRadius={"4px"}
                            />
                          ) : (
                            <Text alignSelf="center">No Image</Text>
                          )}
                        </Flex>
                      );
                    } else if (cell.column.Header === "Platform") {
                      data = (
                        <Text color={textColor} fontSize="sm" fontWeight="500">
                          {cell.value === 1 ? "Android" : "iOS"}
                        </Text>
                      );
                    } else if (cell.column.Header === "Scheduled At") {
                      data = (
                        <Text color={textColor} fontSize="sm" fontWeight="500">
                          {moment(cell.value).format("DD/MM/YYYY")}
                        </Text>
                      );
                    } else {
                      data = (
                        <Text color={textColor} fontSize="sm" fontWeight="500">
                          {cell.value}
                        </Text>
                      );
                    }
                    return (
                      <Td
                        {...cell.getCellProps()}
                        key={index}
                        fontSize={{ sm: "14px" }}
                        maxH="30px !important"
                        py="8px"
                        minW={{ sm: "150px", md: "200px", lg: "2px" }}
                        borderColor="transparent"
                      >
                        <Skeleton
                          borderRadius={16}
                          endColor={"rgba(53,63,255,0.17)"}
                          isLoaded={!isLoading}
                        >
                          {data}
                        </Skeleton>
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Card>
    </>
  );
}
